import * as THREE from 'three';
import { WebGPURenderer } from 'three/webgpu';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { Inspector } from 'three/addons/inspector/Inspector.js';
import { REQUESTED_MAX_STORAGE_BUFFERS_PER_SHADER_STAGE } from '../gpuLimits';

export interface RenderContext {
  renderer: InstanceType<typeof WebGPURenderer>;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  controls: OrbitControls;
  device: GPUDevice;
}

export type InitRendererOptions = {
  enableInspector?: boolean;
  antialias?: boolean;
  maxPixelRatio?: number;
};

export async function initRenderer(options: InitRendererOptions = {}): Promise<RenderContext> {
  const adapter = await navigator.gpu?.requestAdapter();
  if (!adapter) throw new Error('WebGPU not supported');

  const requiredFeatures: GPUFeatureName[] = [];
  if (adapter.features.has('timestamp-query')) requiredFeatures.push('timestamp-query');
  if (adapter.features.has('subgroups' as GPUFeatureName)) requiredFeatures.push('subgroups' as GPUFeatureName);
  const requiredStorageBuffers = Math.min(
    REQUESTED_MAX_STORAGE_BUFFERS_PER_SHADER_STAGE,
    adapter.limits.maxStorageBuffersPerShaderStage,
  );
  if (requiredStorageBuffers < REQUESTED_MAX_STORAGE_BUFFERS_PER_SHADER_STAGE) {
    throw new Error(
      `This demo requires ${REQUESTED_MAX_STORAGE_BUFFERS_PER_SHADER_STAGE} storage buffers per shader stage, but this adapter only supports ${adapter.limits.maxStorageBuffersPerShaderStage}.`,
    );
  }

  const device = await adapter.requestDevice({
    requiredFeatures,
    requiredLimits: {
      maxBufferSize: adapter.limits.maxBufferSize,
      maxStorageBufferBindingSize: adapter.limits.maxStorageBufferBindingSize,
      maxStorageBuffersPerShaderStage: requiredStorageBuffers,
    },
  });

  const renderer = new WebGPURenderer({
    canvas: document.createElement('canvas'),
    antialias: options.antialias ?? true,
    forceWebGL: false,
    device,
  } as any);
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFShadowMap;
  // Match the reference presentation pipeline at the renderer boundary.  AO
  // and SMAA operate in the linear working space; the renderer owns the one
  // final ACES + sRGB conversion when the frame reaches the canvas.
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  // The reference engine's prefiltered environment contributes substantial
  // diffuse energy. This project intentionally carries no CPU/image upload
  // dependency for that asset, so a small exposure lift balances the analytic
  // area/directional/hemisphere equivalent.
  renderer.toneMappingExposure = 1.2;
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  if (options.enableInspector ?? false) {
    // Enable built-in THREE inspector UI (parameters + GPU profiler tabs).
    (renderer as any).inspector = new Inspector();
  }

  // Bound raster work independently of OS scaling. A 3x display scale would
  // otherwise multiply fragment and MSAA work by nine.
  renderer.setPixelRatio(Math.min(
    window.devicePixelRatio,
    Math.max(0.5, options.maxPixelRatio ?? 2.0),
  ));
  // The neutral matte background is continuous with the demo ground and does
  // not tint the GTAO resolve the way the old blue clear color did.
  const matteBackground = new THREE.Color().setRGB(
    0.22,
    0.22,
    0.21,
    THREE.LinearSRGBColorSpace,
  );
  renderer.setClearColor(matteBackground);
  // CSS owns the visible canvas size. Keeping the backing-store resize
  // separate lets main.ts apply it only when the GPU queue is drained and
  // immediately render the first frame at the new size, avoiding a transient
  // cleared canvas being presented during resize.
  renderer.setSize(window.innerWidth, window.innerHeight, false);
  document.body.appendChild(renderer.domElement);

  // Must run after canvas is attached so Inspector can mount its DOM panel.
  if (renderer.init) {
    await renderer.init();
  }

  // Scene
  const scene = new THREE.Scene();
  // PassNode render targets do not inherit the swapchain clear color on every
  // backend. An explicit scene background keeps native, GTAO, SMAA and resize
  // frames on the same neutral matte instead of flashing or resolving black.
  scene.background = matteBackground.clone();

  // Camera
  const camera = new THREE.PerspectiveCamera(
    60, window.innerWidth / window.innerHeight, 0.1, 200
  );
  camera.position.set(0, 8, 16);

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.target.set(0, 3, 0);
  controls.update();

  return { renderer, scene, camera, controls, device };
}
